import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import { addToast } from 'components/Toast/Toast'
import { MainSettingsPage } from 'pages/settings/layout/MainSettingsPage'
import { putProfileSettings } from 'services/endpoints/settings/account/profileSettings'
import { useUserProfile } from 'store'

export const ProfileSettings = () => {
    const [profileSettingsState, setProfileSettingsState] = useState<
        Record<string, boolean>
    >({
        like_count: false,
        follow_count: false,
        show_online_status: false,
        public_friends: false,
        show_mini_bio: false,
        allow_camshow_request: false
    })

    const userData = useUserProfile()!
    const queryClient = useQueryClient()
    const { t } = useTranslation()

    useEffect(() => {
        if (userData) {
            setProfileSettingsState({
                like_count: userData.like_count,
                follow_count: userData.follow_count,
                show_online_status: userData.show_online_status,
                public_friends: userData.public_friends || false,
                show_mini_bio: userData.show_mini_bio || false,
                allow_camshow_request: userData.allow_camshow_request || false
            })
        }
    }, [userData])

    const toggleFunction = useMutation(
        async (newProfileSettings: { key: string; value: boolean }) => {
            const { key, value } = newProfileSettings
            setProfileSettingsState({ ...profileSettingsState, [key]: value })
            return await putProfileSettings({ [key]: value })
        },
        {
            onMutate: async (newData: any) => {
                await queryClient.cancelQueries('user-profile')
                const previousQueryData =
                    queryClient.getQueryData('user-profile')
                queryClient.setQueryData('user-profile', (oldData: any) => {
                    return {
                        ...oldData,
                        [newData.key]: newData.value
                    }
                })
                return { previousQueryData }
            },
            onError: (error, _, context: any) => {
                console.error(error)
                queryClient.setQueryData(
                    'user-profile',
                    context.previousQueryData
                )
                addToast('error', t('error:errorSomethingWentWrong'))
            }
        }
    )

    useEffect(() => {
        return () => {
            queryClient.refetchQueries(['profile', `${userData.id}`])
            queryClient.invalidateQueries('user-profile')
        }
    }, [])

    const profileSettingsCards = [
        {
            body: t('showLikesCount'),
            info: t('controlWhoCanSeeTheLikes'),
            hasToggle: true,
            toggleActive: profileSettingsState.like_count,
            toggleFn: () => {
                toggleFunction.mutate({
                    key: 'like_count',
                    value: !profileSettingsState.like_count
                })
            },
            hasArrow: false
        },
        {
            body: t('showFollowerCount'),
            info: t('controlWhoCanSeeTheFollowers'),
            hasToggle: true,
            toggleActive: profileSettingsState.follow_count,
            toggleFn: () => {
                toggleFunction.mutate({
                    key: 'follow_count',
                    value: !profileSettingsState.follow_count
                })
            },
            hasArrow: false
        },
        {
            body: t('showOnlineStatus'),
            info: t('controlWhoCanSeeYourOnlineStatus'),
            hasToggle: true,
            toggleActive: profileSettingsState.show_online_status,
            toggleFn: () => {
                toggleFunction.mutate({
                    key: 'show_online_status',
                    value: !profileSettingsState.show_online_status
                })
            },
            hasArrow: false
        },
        {
            body: t('showFriendsCount'),
            info: t('controlWhoSeesYourFriendsCount'),
            hasToggle: true,
            toggleActive: profileSettingsState.public_friends,
            toggleFn: () => {
                toggleFunction.mutate({
                    key: 'public_friends',
                    value: !profileSettingsState.public_friends
                })
            },
            hasArrow: false
        },
        {
            body: t('showProfileBio'),
            info: t('controlWhoSeesYourMiniBioThatIsOnYourProfile'),
            hasToggle: true,
            toggleActive: profileSettingsState.show_mini_bio,
            toggleFn: () => {
                toggleFunction.mutate({
                    key: 'show_mini_bio',
                    value: !profileSettingsState.show_mini_bio
                })
            },
            link: '/settings/general/profile-settings/mini-bio',
            hasArrow: true
        }
    ]

    return <MainSettingsPage settingsTop={profileSettingsCards} />
}
